import React from 'react';
import { Check, CheckSquare, Sparkles } from 'lucide-react';
import { DesktopPagination, EmptyState, Button } from '../common';
import {
  getReviewStatusDisplay, getFlagDisplay, getAnswerTypeDisplay,
  getAnalysisStatusDisplay,
} from './ocrApi';
import { InlineMath } from 'react-katex';
import parseAnswerForDisplay from './answerLatex';

/**
 * OCR 문제 목록 (필터 + 표 + 페이지네이션).
 *
 * 상태를 갖지 않는다 — 항목·필터·페이지는 전부 부모가 쥐고 내려준다.
 * 검수 모달 좌측 '목록' 탭이 이 컴포넌트를 쓰는데, 모달을 렌더하는 쪽이 상태를 가지면
 * 목록 → 모달 → 목록 순환이 되기 때문이다. 여기는 모달을 모른다.
 */

export const PROBLEM_FILTERS = [
  { key: 'all', label: '전체' },
  // AI 분석이 정답·풀이를 만들면 그 문제는 검수대기(100)로 되돌아온다 — 그것만 모아 보는 입구
  { key: 'pending', label: '검수대기' },
  { key: 'flagged', label: '플래그 있음' },
  { key: 'conflict', label: '충돌' },
];

// 구성 뱃지: has_* 3개를 ●/○로.
// 정답은 '있다/없다'에 값까지 함께 보인다 — AI 분석이 만들어 낸 결과를 확인하는 자리인데
// 값을 보려면 문제를 한 건씩 열어야 했다(목록 응답이 answer를 이미 주고 있었다).
function CompositionCell({ item }) {
  const answerText = item.has_answer && item.answer != null ? String(item.answer) : '';
  // 판정은 모달과 같은 정본(answerLatex)을 쓴다 — 같은 정답이 목록(원문)과 모달(KaTeX)에서
  // 다른 얼굴이면 answerLatex 머리 주석이 경계한 바로 그 상태가 된다. 수식이면 렌더하고,
  // 렌더 불가·긴 값은 종전대로 원문 절단(전문은 title).
  const shown = answerText ? parseAnswerForDisplay(answerText) : { mode: 'empty', value: '' };
  const truncated = answerText.length > 10 ? `${answerText.slice(0, 10)}…` : answerText;
  const answerNode = shown.mode === 'math' ? (
    <span className="ml-0.5 inline-block max-w-[130px] overflow-hidden whitespace-nowrap align-bottom font-medium text-gray-900" title={answerText}>
      <InlineMath
        math={shown.value}
        renderError={() => <code className="rounded bg-gray-100 px-1 text-[0.9em] text-gray-700">{truncated}</code>}
      />
    </span>
  ) : shown.mode === 'text' ? (
    <span className="ml-0.5 font-medium text-gray-900" title={answerText}>{truncated}</span>
  ) : null;
  const parts = [
    { label: '본문', has: !!item.has_body },
    { label: '정답', has: !!item.has_answer, node: answerNode },
    { label: '풀이', has: !!item.has_solution },
  ];
  return (
    <span className="inline-flex items-center gap-1.5 text-xs">
      {parts.map((p) => (
        <span key={p.label} className={p.has ? 'text-gray-800' : 'text-gray-300'}>
          {p.has ? '●' : '○'} {p.label}
          {p.node}
        </span>
      ))}
    </span>
  );
}

function OcrProblemList({
  items = [],
  loading = false,
  loadError = null,
  filter = 'all',
  onFilterChange,
  totalItems = 0,
  page = 1,
  totalPages = 1,
  onPageChange,
  checkedIds,
  onClearChecked,
  selectedProbId = null,
  onSelect,
  onRetry,
  onApproveAll,
  approving = null, // { done, total } — 진행 중
  approveResult = null, // { text, tone }
  canApprove = true,
  onReanalyze = null, // 목록에서 거는 전체 분석(대상: 승인됐고 아직 분석 안 한 문제)
  analyzeLabel = '다시 분석', // 아직 한 번도 안 돌린 프로젝트에서는 'AI 분석'
  reanalyzing = false,
}) {
  const checked = checkedIds instanceof Set ? checkedIds : new Set();
  const resultTone = {
    green: 'text-green-700', amber: 'text-amber-700', red: 'text-red-600', gray: 'text-gray-500',
  }[approveResult?.tone] || 'text-gray-500';

  return (
    <div className="space-y-3">
      {/* 필터·페이지 이동은 목록 위에 두고 스크롤에도 붙여 둔다.
          아래에 두면 긴 목록에서 화면 밖으로 밀려, 페이지를 넘길 때마다 끝까지 내려가야 했다.
          위로 옮기기만 하면 이번엔 위로 올라가야 하므로 sticky까지 함께 건다.
          음수 마진은 이 컴포넌트를 담는 쪽의 안쪽 여백(p-3)을 덮는 값이다 — 그러지 않으면
          붙었을 때 그 여백 틈으로 목록이 지나가는 것이 비친다. 담는 자리가 바뀌면 이 값도 따라간다. */}
      <div className="sticky top-0 z-10 -mx-3 -mt-3 space-y-2 border-b border-gray-200 bg-gray-50 px-3 pb-2 pt-3">
        {/* 필터 */}
        <div className="flex flex-wrap items-center gap-1">
          {PROBLEM_FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => onFilterChange && onFilterChange(f.key)}
              className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                filter === f.key ? 'bg-primary/10 font-bold text-primary' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
              }`}
            >
              {f.label}
            </button>
          ))}
          {!loading && checked.size > 0 && (
            <span className="ml-auto flex items-center gap-1.5 text-xs text-gray-500">
              <Check className="size-3.5 text-primary" />
              이번에 확인 {checked.size}건
              {onClearChecked && (
                <button
                  type="button"
                  onClick={onClearChecked}
                  className="rounded-full px-1.5 py-0.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                >
                  지우기
                </button>
              )}
            </span>
          )}
          {/* 걸러진 목록을 '전체'로 오해하면 문제가 사라진 줄 안다 — 필터가 걸렸을 때는 그 사실을 함께 적는다 */}
          <span className={`flex items-center gap-1.5 text-xs text-gray-400 ${checked.size > 0 ? '' : 'ml-auto'}`}>
            {!loading && (filter === 'all'
              ? `${totalItems}건`
              : `${PROBLEM_FILTERS.find((f) => f.key === filter)?.label ?? filter} ${totalItems}건만 보는 중`)}
            {!loading && filter !== 'all' && onFilterChange && (
              <button
                type="button"
                onClick={() => onFilterChange('all')}
                className="rounded-full px-1.5 py-0.5 font-medium text-primary transition-colors hover:bg-primary/10"
              >
                전체 보기
              </button>
            )}
          </span>
          {onReanalyze && (
            <Button
              size="xs"
              variant="primary-outline"
              // 인자 없이 부른다 — 바로 넘기면 클릭 이벤트가 ocr_prob_ids 자리로 들어간다
            onClick={() => onReanalyze()}
              loading={reanalyzing}
              disabled={reanalyzing || !canApprove}
              icon={<Sparkles className="size-3.5" />}
            >
              {analyzeLabel}
            </Button>
          )}
          {onApproveAll && (
            <Button
              size="xs"
              variant="success"
              onClick={onApproveAll}
              loading={!!approving}
              disabled={!!approving || loading || !canApprove || items.length === 0}
              icon={<CheckSquare className="size-3.5" />}
            >
              {approving?.total ? `승인 중 ${approving.done}/${approving.total}` : '전체 승인'}
            </Button>
          )}
        </div>

        {/* 결과는 필터 줄 아래 — 거부된 건이 있으면 그 사실이 목록보다 먼저 읽혀야 한다 */}
        {approveResult && (
          <p className={`text-xs ${resultTone}`}>{approveResult.text}</p>
        )}

        {!loading && items.length > 0 && totalPages > 1 && (
          <DesktopPagination
            currentPage={page}
            totalPages={totalPages}
            onPageChange={(n) => onPageChange && onPageChange(n)}
          />
        )}
      </div>

      {/* 목록 */}
      {loading ? (
        <div className="py-8 text-center text-sm text-gray-500">로딩 중...</div>
      ) : items.length > 0 ? (
        <div className="overflow-x-auto rounded-xl border">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                {/* 인덱스 → 검수 → AI 분석. 이 목록은 「AI 결과 확인」으로 여는 자리인데
                    분석 과정이 열로 없어 진행을 훑을 수 없었다. 구성(● 본문·정답·풀이)은
                    문제가 무엇으로 이뤄졌는지라 인덱스 칸으로 내려 본문 미리보기와 붙였다. */}
                <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">인덱스</th>
                <th className="px-3 py-2 text-center text-xs font-medium uppercase tracking-wider text-gray-500">검수</th>
                <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">AI 분석</th>
              </tr>
            </thead>
            <tbody className="bg-white">
              {items.map((p) => {
                const reviewInfo = getReviewStatusDisplay(p.review_status);
                // 분석완료도 배지를 낸다 — '안 된 것만 표시'는 완료를 침묵으로 읽게 해서,
                // 분석이 끝났는지 아직인지 눈으로 확정할 수 없었다. 상태가 없는 응답만 넘긴다.
                const analysisBadge = p.analysis_status != null
                  ? getAnalysisStatusDisplay(p.analysis_status) : null;
                const flags = Array.isArray(p.flags) ? p.flags : [];
                return (
                  <tr
                    key={p.ocr_prob_id}
                    onClick={() => onSelect && onSelect(p.ocr_prob_id)}
                    className={`cursor-pointer border-b border-gray-100 transition-colors duration-150 last:border-b-0 ${
                      String(selectedProbId) === String(p.ocr_prob_id) ? 'bg-primary/15' : 'hover:bg-primary/10'
                    }`}
                  >
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1.5">
                        {/* 이번에 Enter로 확인하고 넘어간 문제 — 검수 상태(오른쪽 배지)와는 별개의 내 진행 표시 */}
                        {checked.has(p.ocr_prob_id) && (
                          <Check className="size-3.5 shrink-0 text-primary" aria-label="이번에 확인함" />
                        )}
                        <span className="text-sm font-medium text-gray-900">{p.problem_number}</span>
                        {p.scope && p.scope !== '__single__' && (
                          <span className="text-xs text-gray-400">({p.scope})</span>
                        )}
                        {/* 객관식/서답형 — 인덱스를 등장 순서로 매기고 나면 지면의 「서답형 1」 같은
                            표기와 인덱스가 달라진다. 유형만은 목록에서도 눈에 보이게 둔다.
                            (2026-08-11 목록 응답에 answerType이 실리면서 상세를 열지 않아도 알 수 있게 됐다) */}
                        {p.answerType && (
                          <span className={`inline-flex shrink-0 items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium ${getAnswerTypeDisplay(p.answerType).cls}`}>
                            {getAnswerTypeDisplay(p.answerType).text}
                          </span>
                        )}
                        {/* 인덱스 충돌로 본문이 분리된 행 — 검수 모달에서 인덱스 수정 시 자동 해소(백엔드 처리) */}
                        {p.is_split && (
                          <span className="inline-flex shrink-0 items-center rounded-full bg-orange-100 px-1.5 py-0.5 text-[10px] font-medium text-orange-700">
                            충돌 분리
                          </span>
                        )}
                      </div>
                      {/* 본문 미리보기(stem 앞 60자·LaTeX 원문) — 충돌 분리 행 구분용, 렌더 없이 회색 원문 */}
                      {p.body_preview && (
                        <div className="mt-0.5 max-w-[240px] truncate text-xs text-gray-400" title={p.body_preview}>
                          {p.body_preview}
                        </div>
                      )}
                      {/* 구성(● 본문 · 정답 값 · 풀이) — 문제가 무엇으로 이뤄졌는지라
                          본문 미리보기 바로 아래가 제자리다. 열을 하나 쓰던 것을 여기로 내리고
                          그 자리를 「AI 분석」에 내줬다. */}
                      <div className="mt-1">
                        <CompositionCell item={p} />
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-center">
                      <span className="inline-flex items-center gap-1">
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${reviewInfo.cls}`}>
                          {reviewInfo.text}
                        </span>
                        {p.promoted === true && (
                          <span className="inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800">
                            등록됨
                          </span>
                        )}
                      </span>
                    </td>
                    {/* AI 분석 — 상태와 플래그를 함께 둔다. 둘은 실제로 한 몸이다:
                        11개 프로젝트 245문제 전수에서 분석 미실행 188건은 **전부** 플래그가 있고
                        분석완료 57건은 **하나도** 없다. 플래그(정답없음·풀이없음이 370건 중 364건)는
                        곧 'AI가 아직 채우지 못한 것'이라, 상태 배지와 같은 칸에서 읽혀야 한다.
                        드물게 섞이는 본문중복 6건도 전부 미실행 행이라 분석이 안 된 이유로 읽힌다. */}
                    <td className="px-3 py-2">
                      <span className="flex flex-wrap items-center gap-1">
                        {analysisBadge && (
                          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${analysisBadge.cls}`}>
                            {analysisBadge.text}
                          </span>
                        )}
                        {flags.map((code) => {
                          const fi = getFlagDisplay(code);
                          return (
                            <span key={code} title={fi.hint || undefined} className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${fi.cls}`}>
                              {fi.text}
                            </span>
                          );
                        })}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <EmptyState
          message={loadError
            ? '문제 목록을 불러올 수 없습니다'
            : filter !== 'all' ? '조건에 맞는 문제가 없습니다' : '문제가 없습니다'}
          description={loadError
            || (filter !== 'all' ? '필터를 변경해 보세요.' : '매칭을 실행하면 검수 대상 문제가 여기에 표시됩니다.')}
          action={loadError && onRetry ? (
            <button
              type="button"
              onClick={onRetry}
              className="rounded-full bg-gray-100 px-4 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-200"
            >
              다시 시도
            </button>
          ) : undefined}
        />
      )}
    </div>
  );
}

export default OcrProblemList;
